class RecentlyViewedProducts extends HTMLElement {
  static STORAGE_KEY = 'recently-viewed-products';
  static MAX_STORED = 30;

  connectedCallback() {
    const currentHandle = this.dataset.productHandle;
    const limit = parseInt(this.dataset.limit, 10) || 10;

    if (currentHandle) this.trackProduct(currentHandle);

    const handles = this.getStoredHandles()
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);

    if (handles.length === 0) {
      this.closest('.recently-viewed-wrapper')?.remove();
      this.remove();
      return;
    }

    Promise.all(handles.map((handle) => this.fetchProduct(handle))).then((products) => {
      const valid = products.filter(Boolean);
      if (valid.length === 0) {
        this.closest('.recently-viewed-wrapper')?.remove();
        this.remove();
        return;
      }
      this.renderProducts(valid);
    });
  }

  trackProduct(handle) {
    try {
      const handles = this.getStoredHandles().filter((h) => h !== handle);
      handles.unshift(handle);
      localStorage.setItem(
        RecentlyViewedProducts.STORAGE_KEY,
        JSON.stringify(handles.slice(0, RecentlyViewedProducts.MAX_STORED))
      );
    } catch (_) {}
  }

  getStoredHandles() {
    try {
      return JSON.parse(localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY)) || [];
    } catch (_) {
      return [];
    }
  }

  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    if (typeof cents !== 'number') return '';
    const amount = (cents / 100).toFixed(2);
    const amountNoCents = String(Math.floor(cents / 100));
    const amountWithComma = amount.replace('.', ',');
    const amountNoCentsWithComma = amountNoCents.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoCents)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoCentsWithComma);
  }

  esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Shopify CDN supports `?width=N` for on-the-fly resizing.
  imgUrl(src, width) {
    if (!src) return '';
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}width=${width}`;
  }

  // ──────── Icon snippets (kept verbatim from card-product-collection.liquid) ────────

  bagIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.62809 0H0.719413L0.0106533 10.9563C-0.0322462 11.6022 0.0547157 12.2502 0.266138 12.8601C0.477559 13.47 0.808926 14.0288 1.23966 14.5018C1.67039 14.9748 2.19129 15.3519 2.77001 15.6096C3.34873 15.8673 3.97291 16.0002 4.60378 16H11.3968C12.0269 16.0001 12.6502 15.8675 13.2283 15.6103C13.8064 15.3532 14.3268 14.977 14.7574 14.505C15.188 14.0331 15.5196 13.4754 15.7316 12.8666C15.9436 12.2578 16.0315 11.6109 15.99 10.9658L15.272 0H3.62809ZM3.62809 0.94451H1.58465L0.931121 11.0224C0.897075 11.5388 0.966787 12.0568 1.13593 12.5444C1.30508 13.032 1.57005 13.4787 1.91441 13.8569C2.25877 14.235 2.67518 14.5366 3.1378 14.7427C3.60043 14.9489 4.09941 15.0554 4.60378 15.0555H11.3968C11.902 15.0567 12.402 14.9512 12.8657 14.7456C13.3294 14.54 13.7469 14.2386 14.0923 13.8603C14.4376 13.482 14.7034 13.0349 14.8731 12.5467C15.0428 12.0584 15.1128 11.5396 15.0787 11.0224L14.416 0.94451H12.3725V1.53955C12.3725 2.12872 12.2594 2.71211 12.0397 3.25643C11.82 3.80075 11.4979 4.29533 11.0919 4.71193C10.6859 5.12853 10.2039 5.459 9.67349 5.68447C9.14302 5.90993 8.57448 6.02597 8.00031 6.02597C7.42614 6.02597 6.85759 5.90993 6.32713 5.68447C5.79667 5.459 5.31468 5.12853 4.90868 4.71193C4.50269 4.29533 4.18063 3.80075 3.96091 3.25643C3.74118 2.71211 3.62809 2.12872 3.62809 1.53955V0.94451ZM11.4521 0.94451H4.54856V1.53955C4.54856 2.47892 4.91222 3.37982 5.55955 4.04406C6.20688 4.7083 7.08485 5.08146 8.00031 5.08146C8.91577 5.08146 9.79374 4.7083 10.4411 4.04406C11.0884 3.37982 11.4521 2.47892 11.4521 1.53955V0.94451Z" fill="currentColor"/></svg>';
  }

  bellIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.5V3M4 7C4 4.79 5.79 3 8 3C10.21 3 12 4.79 12 7V8.5C12 9.43 12.3 10.34 12.87 11.07L13.78 12.22C14.03 12.55 13.79 13 13.38 13H2.62C2.21 13 1.97 12.55 2.22 12.22L3.13 11.07C3.7 10.34 4 9.43 4 8.5V7Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.5 13.5C6.5 14.33 7.17 15 8 15C8.83 15 9.5 14.33 9.5 13.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  arrowIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="9" height="14" viewBox="0 0 9 14" fill="none" aria-hidden="true"><path d="M0.707031 0.707092L6.70703 6.70709L0.707031 12.7071" stroke="#C75E43" stroke-width="2"/></svg>';
  }

  heartIcon() {
    return '<svg width="16" height="14" viewBox="0 0 26 22" fill="none" aria-hidden="true"><path d="M23.8185 6.71922C23.8185 5.93296 23.716 5.24926 23.5109 4.66811C23.3058 4.08696 23.0408 3.61691 22.7161 3.25797C22.3913 2.89902 21.9982 2.617 21.5367 2.41189C21.0752 2.20678 20.6308 2.05294 20.2034 1.95039C19.7761 1.84783 19.3061 1.81365 18.7933 1.84783C18.2805 1.88202 17.7507 2.00166 17.2037 2.20677C16.6567 2.41189 16.1269 2.71955 15.6141 3.12978C15.1013 3.54 14.6911 3.88185 14.3834 4.15533C14.0758 4.42881 13.7852 4.71939 13.5117 5.02705C13.3408 5.24926 13.11 5.36036 12.8194 5.36036C12.5289 5.36036 12.2981 5.24926 12.1272 5.02705C11.8879 4.77066 11.5973 4.48009 11.2555 4.15533C10.9136 3.83057 10.5034 3.48872 10.0248 3.12978C9.54622 2.77083 9.01634 2.46316 8.4352 2.20677C7.85405 1.95039 7.32418 1.83074 6.84558 1.84783C6.36699 1.86492 5.89695 1.89911 5.43545 1.95039C4.97395 2.00166 4.52954 2.1555 4.10222 2.41189C3.67491 2.66828 3.28178 2.9503 2.92283 3.25797C2.56389 3.56564 2.29895 4.03568 2.12803 4.66811C1.9571 5.30054 1.85455 5.98424 1.82036 6.71922C1.82036 8.30883 2.71772 10.001 4.51245 11.7957L12.8194 19.7951L21.1264 11.7957C22.9212 10.001 23.8185 8.30883 23.8185 6.71922Z" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
  }

  spinnerHtml() {
    return '<div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></div>';
  }

  // ──────── Section builders ────────

  // Mirrors snippets/product-badge.liquid (show_sold_out: true) plus the
  // collection-card inventory badge. Metafield-driven content (artist
  // subtitle, preorder ship date) is not available from /products/{handle}.js
  // and is intentionally omitted.
  buildBadgesHtml(product) {
    const tags = product.tags || [];
    const backInStockTag = (this.dataset.backInStockMatch || '').trim();
    const invThreshold = parseInt(this.dataset.invThreshold, 10) || 0;
    const onSale = product.available && product.compare_at_price_min > product.price_min;

    let inner = '';
    const isComingSoon = tags.includes('coming soon');
    if (isComingSoon) {
      inner += '<span class="product-badge product-badge--coming-soon">COMING SOON</span>';
    } else if (product.available) {
      const hasBackInStock = backInStockTag && tags.includes(backInStockTag);
      const isPre = tags.includes('preorder');
      const isBack = tags.includes('backorder');
      const backStyle = `--badge-text: ${this.esc(this.dataset.backInStockText || '')}; --badge-bg: ${this.esc(this.dataset.backInStockBg || '')}; --badge-accent: ${this.esc(this.dataset.backInStockAccent || '')};`;
      if (hasBackInStock && (isPre || isBack)) {
        inner += `<span class="product-badge product-badge--back-in-stock" style="${backStyle}">AVAILABLE AGAIN</span>`;
      } else if (isPre) {
        inner += '<span class="product-badge product-badge--preorder">PRE-ORDER</span>';
      } else if (isBack) {
        inner += '<span class="product-badge product-badge--backorder">BACKORDER</span>';
      } else if (hasBackInStock) {
        inner += `<span class="product-badge product-badge--back-in-stock" style="${backStyle}">BACK IN STOCK</span>`;
      }
      if (onSale) {
        const discount = Math.round(
          ((product.compare_at_price_min - product.price_min) / product.compare_at_price_min) * 100
        );
        inner += `<span class="product-badge product-badge--sale">${discount}% OFF</span>`;
      }
    } else {
      inner = '<span class="product-badge product-badge--sold-out">OUT OF STOCK</span>';
    }

    let invBadge = '';
    if (invThreshold > 0 && product.available) {
      const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
      if (
        variant &&
        variant.inventory_management === 'shopify' &&
        typeof variant.inventory_quantity === 'number' &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity <= invThreshold
      ) {
        const t = this.esc(this.dataset.invTextColor || '');
        const b = this.esc(this.dataset.invBgColor || '');
        const a = this.esc(this.dataset.invAccentColor || '');
        invBadge = `<span class="collection-card__inventory-badge" style="color: ${t}; background-color: ${b}; border-color: ${a}; box-shadow: 1.5px 1.5px 0 0 ${a};">${variant.inventory_quantity} Left</span>`;
      }
    }

    return `<div class="collection-card__badges">${inner ? `<div class="product-badges">${inner}</div>` : ''}${invBadge}</div>`;
  }

  // NEW / TRENDING pill at the bottom-left of the media. Single combined
  // span matches card-product-collection.liquid's current behavior.
  buildCollectionBadgesHtml(product) {
    const tags = product.tags || [];
    const newMatch = (this.dataset.newMatch || '').trim();
    const trendingMatch = (this.dataset.trendingMatch || '').trim();
    const showNew = newMatch && tags.includes(newMatch);
    const showTrending = trendingMatch && tags.includes(trendingMatch);
    if (!showNew && !showTrending) return '';
    let label;
    let variant;
    if (showNew && showTrending) {
      label = 'New & Trending';
      variant = 'new-trending';
    } else if (showNew) {
      label = 'New';
      variant = 'new';
    } else {
      label = 'Trending';
      variant = 'trending';
    }
    return `<div class="collection-card__collection-badges" data-badge-variant="${variant}"><span class="collection-card__collection-badge">${label}</span></div>`;
  }

  buildPriceHtml(product) {
    const onSale = product.compare_at_price_min > product.price_min;
    const soldOut = !product.available;
    const price = this.formatMoney(product.price_min);
    const compareAt = onSale ? this.formatMoney(product.compare_at_price_min) : '';
    const cls = ['price'];
    if (onSale) cls.push('price--on-sale');
    if (soldOut) cls.push('price--sold-out');
    return `<div class="${cls.join(' ')}">
      <div class="price__container">
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular">${price}</span>
        </div>
        <div class="price__sale">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span><s class="price-item price-item--regular">${compareAt}</s></span>
          <span class="visually-hidden visually-hidden--inline">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${price}</span>
        </div>
      </div>
    </div>`;
  }

  buildImageHtml(media, index, alt) {
    const rawSrc =
      media.src ||
      (media.preview_image && media.preview_image.src) ||
      '';
    if (!rawSrc) return '';
    const w = media.width || (media.preview_image && media.preview_image.width) || 533;
    const h = media.height || (media.preview_image && media.preview_image.height) || 533;
    const srcset = [165, 360, 533, 720, 940, 1066]
      .filter((width) => width <= w)
      .map((width) => `${this.imgUrl(rawSrc, width)} ${width}w`)
      .join(', ');
    const isFirst = index === 0;
    const isHoverNext = index === 1;
    let cls = 'collection-card__image';
    if (isFirst) cls += ' is-active';
    if (isHoverNext) cls += ' is-hover-next';
    return `<img
      class="${cls}"
      src="${this.esc(this.imgUrl(rawSrc, 533))}"
      ${srcset ? `srcset="${srcset}"` : ''}
      sizes="(min-width: 990px) 250px, calc((100vw - 30px) / 2)"
      alt="${this.esc(isFirst ? alt : '')}"
      width="${w}"
      height="${h}"
      ${isFirst ? '' : 'loading="lazy" aria-hidden="true"'}
    >`;
  }

  buildArrowsHtml() {
    const prevLabel = this.esc(this.dataset.labelPrevious || 'Previous image');
    const nextLabel = this.esc(this.dataset.labelNext || 'Next image');
    return `<button type="button" class="collection-card__media-arrow collection-card__media-arrow--prev" aria-label="${prevLabel}" data-collection-card-prev>${this.arrowIcon()}</button><button type="button" class="collection-card__media-arrow collection-card__media-arrow--next" aria-label="${nextLabel}" data-collection-card-next>${this.arrowIcon()}</button>`;
  }

  buildDotsHtml(count) {
    let html = '<div class="collection-card__dots" aria-hidden="true" data-collection-card-dots>';
    for (let i = 0; i < count; i++) {
      html += `<span class="collection-card__dot${i === 0 ? ' is-active' : ''}"></span>`;
    }
    html += '</div>';
    return html;
  }

  // Action buttons mirror card-product-collection.liquid's three branches:
  //   1. unavailable          → Notify Me anchor (PDP#notify-me)
  //   2. single avail variant → direct ATC button (data-collection-card-atc)
  //   3. multi-variant        → trigger button + popover (handler in collection-card.js)
  buildActionsHtml(product) {
    const tags = product.tags || [];
    const wishlistHtml = this.buildWishlistHtml(product);
    const title = this.esc(product.title);

    if (!product.available || tags.includes('coming soon')) {
      const notifyLabel = this.esc(this.dataset.labelNotify || 'Notify');
      return `<div class="collection-card__actions">
        <a href="${this.esc(product.url)}#notify-me" class="collection-card__atc-btn collection-card__atc-btn--notify" aria-label="${notifyLabel}: ${title}">
          <span class="collection-card__atc-icon">${this.bellIcon()}</span>
          <span class="collection-card__atc-label" aria-hidden="true">${notifyLabel}</span>
        </a>
        ${wishlistHtml}
      </div>`;
    }

    const variants = product.variants || [];
    const atcVariant = variants.find((v) => v.available) || variants[0];
    const singleAvailable = variants.length === 1 && atcVariant && atcVariant.available;

    let atcLabel = this.dataset.labelAtc || 'Add to cart';
    if (tags.includes('preorder')) atcLabel = this.dataset.labelPreorder || 'Pre-order';
    else if (tags.includes('backorder')) atcLabel = this.dataset.labelBackorder || 'Backorder';
    atcLabel = this.esc(atcLabel);

    if (singleAvailable && atcVariant) {
      return `<div class="collection-card__actions">
        <button type="button" class="collection-card__atc-btn" data-collection-card-atc data-variant-id="${atcVariant.id}" aria-label="${atcLabel}: ${title}">
          <span class="collection-card__atc-icon">${this.bagIcon()}</span>
          <span class="collection-card__atc-label" aria-hidden="true">${atcLabel}</span>
          ${this.spinnerHtml()}
        </button>
        ${wishlistHtml}
      </div>`;
    }

    const chooseLabel = this.esc(this.dataset.labelChoose || 'Choose options');
    return `<div class="collection-card__actions">
      <div class="collection-card__atc-wrap" data-collection-card-atc-wrap>
        <button type="button" class="collection-card__atc-btn" data-collection-card-atc-trigger aria-haspopup="true" aria-expanded="false" aria-label="${chooseLabel}: ${title}">
          <span class="collection-card__atc-icon">${this.bagIcon()}</span>
          <span class="collection-card__atc-label" aria-hidden="true">${atcLabel}</span>
        </button>
      </div>
      ${wishlistHtml}
    </div>`;
  }

  buildVariantPopoverHtml(product) {
    if (!product.available) return '';
    const variants = product.variants || [];
    if (variants.length <= 1) return '';
    const firstAvailable = variants.find((v) => v.available);
    // Snippet only renders the popover when single_variant_available is false,
    // i.e. product is available but the single-variant fast path doesn't apply.
    if (variants.length === 1 && firstAvailable) return '';

    // /products/{handle}.js returns `options` as objects ({name, position,
    // values}); older shapes return plain strings. Handle both so the label
    // never renders "[object Object]".
    const firstOption = product.options && product.options[0];
    const optionName =
      (firstOption && (typeof firstOption === 'string' ? firstOption : firstOption.name)) || 'Variant';
    const chooseLabel = this.esc(this.dataset.labelChoose || 'Choose options');
    const fallbackImg = product.featured_image || '';

    const swatches = variants
      .map((v) => {
        const swatchImg = (v.featured_image && v.featured_image.src) || fallbackImg;
        const imgHtml = swatchImg
          ? `<img src="${this.esc(this.imgUrl(swatchImg, 96))}" srcset="${this.esc(this.imgUrl(swatchImg, 96))} 1x, ${this.esc(this.imgUrl(swatchImg, 192))} 2x" width="48" height="48" alt="" loading="lazy">`
          : '';
        const cls = `collection-card__variant-swatch${v.available ? '' : ' collection-card__variant-swatch--unavailable'}`;
        const disabled = v.available ? '' : 'disabled';
        const vTitle = this.esc(v.title);
        return `<button type="button" class="${cls}" data-collection-card-atc data-variant-id="${v.id}" aria-label="${vTitle}" title="${vTitle}" ${disabled}>
          ${imgHtml}
          <span class="collection-card__variant-swatch-title">${vTitle}</span>
          ${this.spinnerHtml()}
        </button>`;
      })
      .join('');

    return `<div class="collection-card__variant-popover" role="menu" aria-label="${chooseLabel}">
      <div class="collection-card__variant-popover-body">
        <div class="collection-card__variant-popover-label">Choose ${this.esc(optionName)}</div>
        <div class="collection-card__variant-swatches">${swatches}</div>
      </div>
    </div>`;
  }

  buildWishlistHtml(product) {
    const label = this.esc(this.dataset.labelAddWishlist || 'Add to wishlist');
    return `<button class="collection-card__wishlist-btn st-wishlist-button" type="button" data-type="details" data-custom-icon="true" data-handle="${this.esc(product.handle)}" aria-label="${label}">${this.heartIcon()}</button>`;
  }

  buildCardHtml(product, sectionId) {
    const tags = product.tags || [];
    const mediaItems = (product.media || []).slice(0, 4);
    const hasMedia = mediaItems.length > 0;
    const hasMultiMedia = mediaItems.length > 1;
    const meta = [];
    if (product.vendor) meta.push(this.esc(product.vendor));
    if (product.type) meta.push(this.esc(String(product.type).replace('Compact Disc', 'CD')));
    const metaHtml = meta.length ? `<div class="collection-card__meta">${meta.join(' · ')}</div>` : '';

    const mediaInnerHtml = hasMedia
      ? mediaItems.map((m, i) => this.buildImageHtml(m, i, product.title)).join('')
      : `<div class="collection-card__placeholder"></div>`;

    const cardClass = `collection-card${product.available ? '' : ' collection-card--unavailable'}`;
    const cardTitleId = `CollectionCardTitle-${sectionId}-${product.id}`;
    const mediaDataAttr = hasMultiMedia ? ' data-collection-card-media' : '';

    return `<li class="related-products-slider__slide">
      <div class="${cardClass}" data-tags="${this.esc(tags.join(' ').toLowerCase())}" data-product-id="${product.id}">
        <div class="collection-card__media"${mediaDataAttr}>
          <a href="${this.esc(product.url)}" class="collection-card__media-link" aria-labelledby="${cardTitleId}" tabindex="-1">
            ${mediaInnerHtml}
          </a>
          ${hasMultiMedia ? this.buildArrowsHtml() : ''}
          ${hasMultiMedia ? this.buildDotsHtml(mediaItems.length) : ''}
          ${this.buildBadgesHtml(product)}
          ${this.buildCollectionBadgesHtml(product)}
          ${this.buildActionsHtml(product)}
          ${this.buildVariantPopoverHtml(product)}
        </div>
        <div class="collection-card__info">
          ${metaHtml}
          <h3 class="collection-card__title" id="${cardTitleId}">
            <a href="${this.esc(product.url)}" class="collection-card__title-link">${this.esc(product.title)}</a>
          </h3>
          <div class="collection-card__price">${this.buildPriceHtml(product)}</div>
        </div>
      </div>
    </li>`;
  }

  renderProducts(products) {
    const list = this.querySelector('ul');
    if (!list) return;
    const sectionId = this.dataset.sectionId || 'rv';
    list.innerHTML = products.map((p) => this.buildCardHtml(p, sectionId)).join('');
    this.querySelector('[hidden]')?.removeAttribute('hidden');
    // Branded overlay arrows + dot pagination (shared with related products).
    if (typeof window.initRelatedCarousel === 'function') {
      window.initRelatedCarousel(this);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
